import {
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  SvgIcon,
  SwipeableDrawer,
  Toolbar,
  useTheme,
} from "@mui/material"
import React, { FC, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useAppDispatch } from "@app/hooks"
import {
  getIsAppMenuOpen,
  setMenuButtonVisible,
  setMenuOpen,
  getAuthRole,
} from "@app/store"
import { App } from "@app/interfaces"
import { NavLink } from "react-router-dom"
import { ExpandMore, ExpandLess } from "@mui/icons-material"

interface IAppMenuProps {
  menu: App.RecursiveMenuItem[]
}
interface MenuListItemProps {
  item: App.MenuItem
  depth: number
  url: string
}
interface MenuGroupProps {
  item: App.MenuGroup
  depth: number
  url: string
  keyPrefix: string
}

export const drawerWidth = 440

interface IAppNavLinkProps {
  children: React.ReactElement
  to: string
  props?: [...any]
}

const getIcon = (Icon: typeof SvgIcon) => <Icon />

export const NavListItem: FC<IAppNavLinkProps> = ({
  to,
  children,
  ...props
}): React.ReactElement => {
  const theme = useTheme()
  const dispatch = useAppDispatch()

  return (
    <NavLink
      to={to}
      style={{
        textDecoration: "none",
        color: theme.palette.text.primary,
        width: "100%",
      }}
      onClick={() => dispatch(setMenuOpen(false))}
      children={({ isActive }) => (
        <ListItemButton selected={isActive} {...props}>
          {children}
        </ListItemButton>
      )}
    />
  )
}

export const MenuListItem: FC<MenuListItemProps> = ({ item, depth, url }) => {
  return (
    <ListItem
      key={item.url}
      disablePadding
      style={{ paddingLeft: `${depth * 20}px` }}
    >
      <NavListItem to={url}>
        <>
          {!!item.icon && (
            <ListItemIcon>{item.icon && getIcon(item.icon)}</ListItemIcon>
          )}
          <ListItemText primary={item.title} />
        </>
      </NavListItem>
    </ListItem>
  )
}

const getMenuUrl = (menu: App.RecursiveMenuItem): string | null => {
  if ("url" in menu) {
    return menu.url
  }
  // Recursively find the first descendant that has a url. Groups can end up
  // empty after role/module filtering, so guard against missing children
  // instead of blindly reading `items[0]` (which used to crash the render).
  for (const child of menu.items) {
    const url = getMenuUrl(child)
    if (url) return url
  }
  return null
}

const renderMenuItem = (
  item: App.RecursiveMenuItem,
  depth: number = 0,
  keyPrefix: string = "0",
): JSX.Element[] => {
  const url = getMenuUrl(item)

  // No reachable url (e.g. an empty group) — nothing to render/navigate to.
  if (!url) return []

  if ("items" in item) {
    return [
      <MenuGroup
        key={keyPrefix}
        item={item}
        depth={depth}
        url={url}
        keyPrefix={keyPrefix}
      />,
    ]
  }
  return [<MenuListItem key={keyPrefix} item={item} depth={depth} url={url} />]
}

const MenuGroup: FC<MenuGroupProps> = ({ item, depth, url, keyPrefix }) => {
  const [isOpen, setIsOpen] = useState(!!item.isOpen)
  const handleToggle = () => setIsOpen(!isOpen)

  return (
    <>
      <ListItem disablePadding sx={{ paddingLeft: `${depth * 20}px` }}>
        <NavListItem to={url}>
          <>
            {!!item.icon && (
              <ListItemIcon>{item.icon && getIcon(item.icon)}</ListItemIcon>
            )}
            <ListItemText primary={item.title} />
          </>
        </NavListItem>
        <ListItemButton onClick={handleToggle} sx={{ alignSelf: "stretch" }}>
          {isOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
      </ListItem>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {item.items.flatMap((subItem, i) =>
            renderMenuItem(subItem, depth + 1, `${keyPrefix}-${i}`),
          )}
        </List>
      </Collapse>
    </>
  )
}

export const AppMenu: FC<IAppMenuProps> = (props) => {
  const { menu } = props
  const dispatch = useAppDispatch()
  const open = useSelector(getIsAppMenuOpen)

  useEffect(() => {
    if (menu.length > 0) {
      dispatch(setMenuButtonVisible(true))
    } else {
      dispatch(setMenuButtonVisible(false))
    }
  }, [menu])

  const items = useMemo(
    () => menu.flatMap((item, i) => renderMenuItem(item, 0, `${i}`)),
    [menu],
  )

  return (
    <>
      <Paper elevation={2}>
        <SwipeableDrawer
          anchor={"left"}
          variant="temporary"
          open={open}
          onClose={() => dispatch(setMenuOpen(false))}
          onOpen={() => dispatch(setMenuOpen(true))}
          // Build the menu tree once and just toggle visibility instead of
          // remounting the whole list every time the drawer opens.
          ModalProps={{ keepMounted: true }}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
            },
          }}
        >
          <Toolbar />
          <Divider />
          {/* <List onClick={() => dispatch(setMenuOpen(false))}> */}
          <List>{items}</List>
        </SwipeableDrawer>
      </Paper>
    </>
  )
}
